import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import useRealtimeSubscription from '../../../hooks/useRealtimeSubscription';

const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    dot: 'bg-amber-400',
    pulse: true,
  },
  processing: {
    label: 'Processing',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    dot: 'bg-blue-500',
    pulse: true,
  },
  paid: {
    label: 'Paid',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500',
    pulse: false,
  },
  failed: {
    label: 'Failed',
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    dot: 'bg-red-500',
    pulse: false,
  },
  canceled: {
    label: 'Canceled',
    color: 'text-gray-500',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    dot: 'bg-gray-400',
    pulse: false,
  },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG?.[status] || STATUS_CONFIG?.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg?.bg} ${cfg?.color} ${cfg?.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg?.dot} ${cfg?.pulse ? 'animate-pulse' : ''}`} />
      {cfg?.label}
    </span>
  );
};

const BrandPayoutPanel = ({ brandId }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Stripe balance
  const [stripeBalance, setStripeBalance] = useState({ pendingAmount: 0, availableAmount: 0 });

  // Pending payouts to influencers
  const [pendingPayouts, setPendingPayouts] = useState([]);

  // Payout history
  const [payoutHistory, setPayoutHistory] = useState([]);

  // Campaign budget utilization
  const [campaignUtilization, setCampaignUtilization] = useState([]);

  // Summary stats
  const [summary, setSummary] = useState({
    totalPendingAmount: 0,
    totalPaidAmount: 0,
    pendingCount: 0,
    totalBudget: 0,
    totalSpent: 0,
  });

  // Load pending payouts to influencers (approved transactions not yet paid out)
  const loadPendingPayouts = useCallback(async () => {
    if (!brandId) return;
    try {
      const { data, error } = await supabase
        ?.from('transactions')
        ?.select(`
          id, amount, status, payout_status, stripe_transfer_id, created_at,
          campaigns(title, budget, reward_amount),
          influencer:user_profiles!transactions_influencer_id_fkey(id, full_name, email)
        `)
        ?.eq('brand_id', brandId)
        ?.in('status', ['approved', 'processing'])
        ?.order('created_at', { ascending: false })
        ?.limit(20);

      if (!error && data) {
        const mapped = data?.map(t => ({
          id: t?.id,
          influencerName: t?.influencer?.full_name || t?.influencer?.email?.split('@')?.[0] || 'Influencer',
          influencerEmail: t?.influencer?.email || '',
          campaignName: t?.campaigns?.title || 'Campaign',
          amount: parseFloat(t?.amount) || 0,
          transactionStatus: t?.status,
          payoutStatus: t?.payout_status || 'pending',
          stripeTransferId: t?.stripe_transfer_id || '',
          createdAt: t?.created_at,
          reconciled: !!(t?.stripe_transfer_id),
        }));
        setPendingPayouts(mapped);

        const totalPending = mapped?.reduce((sum, p) => sum + p?.amount, 0);
        const pendingCount = mapped?.filter(p => p?.payoutStatus === 'pending' || p?.payoutStatus === 'processing')?.length;
        setSummary(prev => ({ ...prev, totalPendingAmount: totalPending, pendingCount }));
      }
    } catch (err) {
      console.error('[BrandPayoutPanel] loadPendingPayouts error:', err);
    }
  }, [brandId]);

  // Load payout history (paid transactions)
  const loadPayoutHistory = useCallback(async () => {
    if (!brandId) return;
    try {
      const { data, error } = await supabase
        ?.from('transactions')
        ?.select(`
          id, amount, status, payout_status, stripe_transfer_id, stripe_payout_id, payout_arrival_date, created_at,
          campaigns(title),
          influencer:user_profiles!transactions_influencer_id_fkey(id, full_name, email)
        `)
        ?.eq('brand_id', brandId)
        ?.eq('status', 'approved')
        ?.not('stripe_transfer_id', 'is', null)
        ?.order('created_at', { ascending: false })
        ?.limit(15);

      if (!error && data) {
        const mapped = data
          ?.filter(t => t?.stripe_transfer_id && t?.stripe_transfer_id !== '')
          ?.map(t => ({
            id: t?.id,
            influencerName: t?.influencer?.full_name || t?.influencer?.email?.split('@')?.[0] || 'Influencer',
            campaignName: t?.campaigns?.title || 'Campaign',
            amount: parseFloat(t?.amount) || 0,
            payoutStatus: t?.payout_status || 'paid',
            stripeTransferId: t?.stripe_transfer_id || '',
            stripePayoutId: t?.stripe_payout_id || '',
            arrivalDate: t?.payout_arrival_date,
            createdAt: t?.created_at,
          }));
        setPayoutHistory(mapped);

        const totalPaid = mapped?.reduce((sum, p) => sum + p?.amount, 0);
        setSummary(prev => ({ ...prev, totalPaidAmount: totalPaid }));
      }
    } catch (err) {
      console.error('[BrandPayoutPanel] loadPayoutHistory error:', err);
    }
  }, [brandId]);

  // Load campaign budget utilization vs actual payouts
  const loadCampaignUtilization = useCallback(async () => {
    if (!brandId) return;
    try {
      const { data: campaigns, error: campError } = await supabase
        ?.from('campaigns')
        ?.select('id, title, budget, spent, status')
        ?.eq('brand_id', brandId)
        ?.in('status', ['active', 'completed'])
        ?.order('created_at', { ascending: false })
        ?.limit(8);

      if (!campError && campaigns) {
        // Get actual payout amounts per campaign
        const campaignIds = campaigns?.map(c => c?.id);
        let payoutMap = {};

        if (campaignIds?.length > 0) {
          const { data: txData } = await supabase
            ?.from('transactions')
            ?.select('campaign_id, amount, status')
            ?.in('campaign_id', campaignIds)
            ?.eq('status', 'approved');

          txData?.forEach(t => {
            if (!payoutMap?.[t?.campaign_id]) payoutMap[t.campaign_id] = 0;
            payoutMap[t.campaign_id] += parseFloat(t?.amount) || 0;
          });
        }

        const mapped = campaigns?.map(c => {
          const budget = parseFloat(c?.budget) || 0;
          const actualPayout = payoutMap?.[c?.id] || 0;
          const budgetSpent = parseFloat(c?.spent) || 0;
          const utilization = budget > 0 ? Math.min((budgetSpent / budget) * 100, 100) : 0;
          const payoutRatio = budget > 0 ? Math.min((actualPayout / budget) * 100, 100) : 0;
          return {
            id: c?.id,
            name: c?.title,
            status: c?.status,
            budget,
            budgetSpent,
            actualPayout,
            utilization,
            payoutRatio,
            remaining: Math.max(budget - budgetSpent, 0),
          };
        });

        setCampaignUtilization(mapped);
        const totalBudget = mapped?.reduce((sum, c) => sum + c?.budget, 0);
        const totalSpent = mapped?.reduce((sum, c) => sum + c?.budgetSpent, 0);
        setSummary(prev => ({ ...prev, totalBudget, totalSpent }));
      }
    } catch (err) {
      console.error('[BrandPayoutPanel] loadCampaignUtilization error:', err);
    }
  }, [brandId]);

  // Sync Stripe balance via edge function
  const fetchStripeBalance = useCallback(async () => {
    setStripeLoading(true);
    try {
      const { data, error } = await supabase?.functions?.invoke('stripe-balance', {
        body: {},
      });
      if (!error && data) {
        setStripeBalance({
          pendingAmount: data?.pendingAmount || 0,
          availableAmount: data?.availableAmount || 0,
        });
        setLastUpdated(new Date());
      }
    } catch (err) {
      console.error('[BrandPayoutPanel] fetchStripeBalance error:', err);
    } finally {
      setStripeLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!brandId) return;
    const init = async () => {
      setLoading(true);
      await Promise.all([
        loadPendingPayouts(),
        loadPayoutHistory(),
        loadCampaignUtilization(),
      ]);
      setLoading(false);
      setLastUpdated(new Date());
    };
    init();
  }, [brandId]);

  // Real-time: transactions table for brand
  useRealtimeSubscription({
    channelName: `brand-payout-transactions-${brandId}`,
    table: 'transactions',
    filter: brandId ? `brand_id=eq.${brandId}` : undefined,
    enabled: !!brandId,
    onChange: () => {
      loadPendingPayouts();
      loadPayoutHistory();
      loadCampaignUtilization();
      setLastUpdated(new Date());
    },
  });

  // Real-time: stripe_payouts table
  useRealtimeSubscription({
    channelName: `brand-stripe-payouts-${brandId}`,
    table: 'stripe_payouts',
    enabled: !!brandId,
    onChange: () => {
      loadPendingPayouts();
      loadPayoutHistory();
      setLastUpdated(new Date());
    },
  });

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'pending', label: 'Pending', icon: 'Clock', count: summary?.pendingCount },
    { id: 'history', label: 'History', icon: 'History' },
    { id: 'budget', label: 'Budget', icon: 'PieChart' },
  ];

  const formatCurrency = (val) => `$${(parseFloat(val) || 0)?.toFixed(2)}`;
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const truncateId = (id) => id ? `${id?.slice(0, 8)}...` : '—';

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Banknote" size={18} className="text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">Payout Status</h3>
            {lastUpdated && (
              <p className="text-xs text-muted-foreground">
                Updated {lastUpdated?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={fetchStripeBalance}
          disabled={stripeLoading}
          className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium transition-colors disabled:opacity-50 px-3 py-1.5 rounded-lg border border-primary/20 hover:bg-primary/5"
        >
          <Icon name="RefreshCw" size={13} className={stripeLoading ? 'animate-spin' : ''} />
          Sync Stripe
        </button>
      </div>
      {/* Stripe Balance Bar */}
      <div className="px-5 py-3 bg-gradient-to-r from-primary/5 to-primary/10 border-b border-border flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Icon name="Zap" size={14} className="text-primary" />
          <span className="text-xs font-medium text-muted-foreground">Live Stripe Balance</span>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="text-sm font-bold text-foreground">{formatCurrency(stripeBalance?.availableAmount)}</p>
            <p className="text-xs text-muted-foreground">Available</p>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="text-right">
            <p className="text-sm font-bold text-amber-600">{formatCurrency(stripeBalance?.pendingAmount)}</p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </div>
        </div>
      </div>
      {/* Summary Stats */}
      <div className="grid grid-cols-4 divide-x divide-border border-b border-border">
        <div className="px-4 py-3 text-center">
          <p className="text-base font-bold text-amber-600">{formatCurrency(summary?.totalPendingAmount)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Pending Payouts</p>
        </div>
        <div className="px-4 py-3 text-center">
          <p className="text-base font-bold text-emerald-600">{formatCurrency(summary?.totalPaidAmount)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Total Paid</p>
        </div>
        <div className="px-4 py-3 text-center">
          <p className="text-base font-bold text-foreground">{formatCurrency(summary?.totalBudget)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Total Budget</p>
        </div>
        <div className="px-4 py-3 text-center">
          <p className="text-base font-bold text-blue-600">
            {summary?.totalBudget > 0 ? `${((summary?.totalSpent / summary?.totalBudget) * 100)?.toFixed(0)}%` : '0%'}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">Budget Used</p>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-border overflow-x-auto">
        {tabs?.map(tab => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium whitespace-nowrap transition-colors border-b-2 ${
              activeTab === tab?.id
                ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={13} />
            {tab?.label}
            {tab?.count > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
                {tab?.count}
              </span>
            )}
          </button>
        ))}
      </div>
      {/* Tab Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name="Clock" size={14} className="text-amber-600" />
                      <span className="text-xs font-medium text-amber-700">Awaiting Payout</span>
                    </div>
                    <p className="text-xl font-bold text-amber-700">{summary?.pendingCount}</p>
                    <p className="text-xs text-amber-600 mt-0.5">{formatCurrency(summary?.totalPendingAmount)} total</p>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name="CheckCircle" size={14} className="text-emerald-600" />
                      <span className="text-xs font-medium text-emerald-700">Paid Out</span>
                    </div>
                    <p className="text-xl font-bold text-emerald-700">{payoutHistory?.length}</p>
                    <p className="text-xs text-emerald-600 mt-0.5">{formatCurrency(summary?.totalPaidAmount)} total</p>
                  </div>
                </div>

                {/* Recent activity */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Recent Activity</p>
                  {pendingPayouts?.slice(0, 4)?.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">No pending payouts</p>
                  ) : (
                    <div className="space-y-2">
                      {pendingPayouts?.slice(0, 4)?.map(p => (
                        <div key={p?.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Icon name="User" size={12} className="text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-foreground truncate">{p?.influencerName}</p>
                              <p className="text-xs text-muted-foreground truncate">{p?.campaignName}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-xs font-semibold text-foreground">{formatCurrency(p?.amount)}</span>
                            <StatusBadge status={p?.payoutStatus} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Pending Tab */}
            {activeTab === 'pending' && (
              <div className="space-y-2">
                {pendingPayouts?.length === 0 ? (
                  <div className="text-center py-8">
                    <Icon name="CheckCircle" size={32} className="text-emerald-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-foreground">All caught up!</p>
                    <p className="text-xs text-muted-foreground mt-1">No pending payouts to influencers</p>
                  </div>
                ) : (
                  pendingPayouts?.map(p => (
                    <div key={p?.id} className="border border-border rounded-lg p-3 hover:bg-muted/30 transition-colors">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Icon name="User" size={14} className="text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-foreground truncate">{p?.influencerName}</p>
                            <p className="text-xs text-muted-foreground truncate">{p?.campaignName}</p>
                            <p className="text-xs text-muted-foreground">{formatDate(p?.createdAt)}</p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-bold text-foreground">{formatCurrency(p?.amount)}</p>
                          <StatusBadge status={p?.payoutStatus} />
                          {p?.reconciled && (
                            <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1 justify-end">
                              <Icon name="Link" size={10} />
                              Reconciled
                            </p>
                          )}
                        </div>
                      </div>
                      {p?.stripeTransferId && (
                        <div className="mt-2 pt-2 border-t border-border">
                          <p className="text-xs text-muted-foreground font-mono">
                            Transfer: {truncateId(p?.stripeTransferId)}
                          </p>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div className="space-y-2">
                {payoutHistory?.length === 0 ? (
                  <div className="text-center py-8">
                    <Icon name="History" size={32} className="text-muted-foreground/40 mx-auto mb-2" />
                    <p className="text-sm font-medium text-foreground">No payout history yet</p>
                    <p className="text-xs text-muted-foreground mt-1">Completed payouts will appear here</p>
                  </div>
                ) : (
                  payoutHistory?.map(p => (
                    <div key={p?.id} className="border border-border rounded-lg p-3 hover:bg-muted/30 transition-colors">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <Icon name="User" size={12} className="text-muted-foreground" />
                            <p className="text-xs font-semibold text-foreground truncate">{p?.influencerName}</p>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">{p?.campaignName}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(p?.createdAt)}</p>
                          {p?.stripeTransferId && (
                            <p className="text-xs text-muted-foreground font-mono mt-1">
                              {truncateId(p?.stripeTransferId)}
                            </p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-bold text-emerald-600">{formatCurrency(p?.amount)}</p>
                          <StatusBadge status={p?.payoutStatus || 'paid'} />
                          {p?.arrivalDate && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Arrived {formatDate(p?.arrivalDate)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Budget Tab */}
            {activeTab === 'budget' && (
              <div className="space-y-3">
                {campaignUtilization?.length === 0 ? (
                  <div className="text-center py-8">
                    <Icon name="PieChart" size={32} className="text-muted-foreground/40 mx-auto mb-2" />
                    <p className="text-sm font-medium text-foreground">No campaign data</p>
                    <p className="text-xs text-muted-foreground mt-1">Active campaigns will show budget utilization</p>
                  </div>
                ) : (
                  campaignUtilization?.map(c => (
                    <div key={c?.id} className="border border-border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-foreground truncate">{c?.name}</p>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                            c?.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {c?.status}
                          </span>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs font-bold text-foreground">{formatCurrency(c?.budgetSpent)}</p>
                          <p className="text-xs text-muted-foreground">of {formatCurrency(c?.budget)}</p>
                        </div>
                      </div>

                      {/* Budget utilization bar */}
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Budget Used</span>
                          <span className="text-xs font-medium text-foreground">{c?.utilization?.toFixed(0)}%</span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(c?.utilization, 100)}%` }}
                          />
                        </div>

                        {/* Actual payout bar */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Actual Payouts</span>
                          <span className="text-xs font-medium text-emerald-600">{formatCurrency(c?.actualPayout)}</span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(c?.payoutRatio, 100)}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                        <span className="text-xs text-muted-foreground">Remaining</span>
                        <span className="text-xs font-semibold text-blue-600">{formatCurrency(c?.remaining)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BrandPayoutPanel;
